// ============================================================
// CalendarView.tsx
// Phase 2c sidebar Calendar zone — main-area takeover that
// renders the firm's aggregated deadline feed (task deadlines +
// case-fact deadlines). Loaded by GET /api/calendar via the
// store's loadCalendarEntries action.
//
// Outer shell + AppHeader + ← Back pattern mirrors
// ClientSearchView.tsx exactly. Empty-state recipe is the
// slimmer ClientSearchView-style (no CTA — Calendar has nothing
// to create from here). Loading state is a 3-row skeleton.
//
// Grouping: entries arrive pre-sorted ascending by date from the
// backend. The view buckets them into Overdue → Today →
// Tomorrow → next-6-named-days → calendar-month-and-beyond. The
// "Overdue" header is recoloured red so the user clocks urgency
// at a glance; everything else uses the standard casevo-mid.
//
// Click semantics:
//   - case_deadline → button, opens its case (backend guarantees
//                     a non-null caseId since these are generated
//                     FROM cases).
//   - task w/ caseId → button, opens the linked case.
//   - task w/o caseId → non-clickable div (TODO: open in Tasks
//                       view when that lands).
// ============================================================
import { londonDayOf } from '../utils/civilDate'
import { serverErrorMessage } from '../utils/serverError'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LockIcon, LOCKED_CASE_TIP } from '../components/shared/CaseLink'
import { useCaseAccess } from '../hooks/useCaseAccess'
import { useCasevoStore } from '../store/useCasevoStore'
import { useFirmChange } from '../utils/liveEvents'
import { initialPlaceParam, navigateTo, usePlaceParam } from '../navigation/navigate'
import { CalendarEntry, DayNote, Reminder } from '../types/calendar.types'
import { getAppointmentApi, updateAppointmentApi, updateReminderApi, updateTaskApi, deleteTaskApi, deleteReminderApi, deleteAppointmentApi, moveAppointmentApi, type RecurringMoveScope, updateCaseDeadlineApi, deleteCaseDeadlineApi } from '../api/casevoApi'
import { useCalendarRangeData, calendarRangeCache } from '../components/calendar/useCalendarRangeData'
import type { CalendarRange } from '../components/calendar/calendarRangeCache'
import { flushAutoSave } from '../hooks/useAutoSave'
import { flushClientAutoSave } from '../hooks/useClientAutoSave'
import { flushClientNotesAutoSave } from '../hooks/useClientNotesAutoSave'
import { flushWitnessAutoSave } from '../hooks/useWitnessAutoSave'
import { flushDeadlineAutoSave } from '../hooks/useCaseDeadlineAutoSave'
import AppHeader from '../components/layout/AppHeader'
import BackButton from '../components/layout/BackButton'
import { SidebarHamburger } from '../components/layout/Sidebar'
import CalendarMonthGrid from '../components/calendar/CalendarMonthGrid'
import CalendarTimeGrid, { entryDayKey, reminderDayKey } from '../components/calendar/CalendarTimeGrid'
import CalendarMiniMonth from '../components/calendar/CalendarMiniMonth'
import CalendarContextMenu, { CalendarMenuItem } from '../components/calendar/CalendarContextMenu'
import { MenuIcon } from '../components/shared/menuUi'
import CalendarDayPanel from '../components/calendar/CalendarDayPanel'
import AppointmentFormModal from '../components/calendar/AppointmentFormModal'
import LinkCasePickerModal from '../components/shared/LinkCasePickerModal'
import ConfirmDialogue from '../components/shared/ConfirmDialogue'
import ActionBox from '../components/shared/ActionBox'
import { useCalendarDarkPref } from '../utils/calendarDarkPref'
import { useCalendarScopePref } from '../utils/calendarScopePref'
import { useCalendarViewPref, CalendarViewMode } from '../utils/calendarViewPref'
import { readAnchorDate, writeAnchorDate, readMonthDate, writeMonthDate } from '../utils/calendarAnchorPref'
import { presence } from '../services/presence'
import { subscribeLocalEntityChanges } from '../services/localEntityChanges'

// ── VIEW MODE ──────────────────────────────────────────────
// Stage 2 makes the month GRID the default (and only) Calendar view.
// The grouped-list implementation below (groupCalendarEntries +
// CalendarRow + the store-fed list branch) is kept intact behind this
// flag so a future "list / grid" toggle can flip it back on without a
// rebuild. Change the return below to 'list' to restore the old
// behaviour. Wrapped in a function so TypeScript keeps the union type
// (a bare `const = 'grid'` narrows to the literal, which would make
// the preserved `=== 'list'` branch a compile error).
type CalendarMode = 'grid' | 'list'
const getCalendarMode = (): CalendarMode => 'grid'
const CALENDAR_MODE: CalendarMode = getCalendarMode()

// ── MONTH-RANGE HELPERS ────────────────────────────────────
// The grid shows leading/trailing days from adjacent months, so we
// fetch a padded window (month ± 7 days) to populate those cells too.
// Bounds are built at UTC civil-day edges — entries are stored date-
// only at midnight UTC, so [start-00:00Z, end-23:59Z] is inclusive of
// every day-only row in the window.
const utcStartIso = (d: Date): string =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)).toISOString()
const utcEndIso = (d: Date): string =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)).toISOString()
const monthFetchRange = (monthDate: Date): { from: string; to: string } => {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
  const last = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0)
  const from = new Date(first)
  from.setDate(from.getDate() - 7)
  const to = new Date(last)
  to.setDate(to.getDate() + 7)
  return { from: utcStartIso(from), to: utcEndIso(to) }
}

// ── DAY / WEEK HELPERS (Diary batch) ───────────────────────
// Local YYYY-MM-DD key (matches the grid's civil-day keying).
const ymdLocal = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
// Monday-start of the week containing d, at local midnight.
const startOfWeekMon = (d: Date): Date => {
  const offset = (d.getDay() + 6) % 7 // Mon=0
  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  s.setDate(s.getDate() - offset)
  return s
}
// The 7 local-midnight dates of the week containing `anchor` (Mon-first).
const weekDaysOf = (anchor: Date): Date[] => {
  const start = startOfWeekMon(anchor)
  return Array.from(
    { length: 7 },
    (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i),
  )
}
// The [from,to] fetch window for the active view. Day/Week fetch just
// their own range; Month keeps the padded month window (adjacent cells).
const computeFetchRange = (
  view: CalendarViewMode,
  monthDate: Date,
  anchorDate: Date,
): { from: string; to: string } => {
  if (view === 'month') return monthFetchRange(monthDate)
  if (view === 'week') {
    const w = weekDaysOf(anchorDate)
    return { from: utcStartIso(w[0]), to: utcEndIso(w[6]) }
  }
  return { from: utcStartIso(anchorDate), to: utcEndIso(anchorDate) }
}
// The previous + next range for the active view — prefetched once the current
// range is on screen, so flicking back / forward shows instantly.
const neighbourFetchRanges = (
  view: CalendarViewMode,
  monthDate: Date,
  anchorDate: Date,
): CalendarRange[] => {
  if (view === 'month') {
    return [-1, 1].map((d) => monthFetchRange(new Date(monthDate.getFullYear(), monthDate.getMonth() + d, 1)))
  }
  const step = view === 'week' ? 7 : 1
  return [-step, step].map((d) =>
    computeFetchRange(view, monthDate, new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate() + d)),
  )
}

// ── EMPTY-STATE ICON ───────────────────────────────────────
// Hand-rolled calendar glyph with a filled day-dot so it reads as
// "a calendar with a marked date" rather than a generic calendar.
// 72×72 viewBox at fixed size, mint stroke + currentColor cascade so
// the parent's text-casevo-brand colour carries through.
const CalendarEmptyIcon: React.FC = () => (
  <svg
    width="72"
    height="72"
    viewBox="0 0 72 72"
    fill="none"
    aria-hidden="true"
    className="mb-6 text-casevo-brand opacity-60"
  >
    <rect x="9" y="15" width="54" height="48" rx="4" stroke="currentColor" strokeWidth="2.5" />
    <line x1="22" y1="9" x2="22" y2="21" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="50" y1="9" x2="50" y2="21" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="9" y1="29" x2="63" y2="29" stroke="currentColor" strokeWidth="2.5" />
    <circle cx="36" cy="46" r="4.5" fill="currentColor" />
  </svg>
)

// ── TYPE PILL ──────────────────────────────────────────────
// Small inline pill next to each entry's title. Two variants —
// "Case" for case-fact deadlines (mint, matches existing case-list
// status chips) and "Task" for task deadlines (brand green/white,
// reads as the primary firm-defined work item).
const TypePill: React.FC<{ type: CalendarEntry['type'] }> = ({ type }) => {
  const isTask = type === 'task'
  const colour = isTask
    ? 'bg-casevo-primary text-white'
    : 'bg-casevo-mint text-casevo-forest'
  return (
    <span
      className={`text-[10px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full ${colour} shrink-0`}
    >
      {isTask ? 'Task' : 'Case'}
    </span>
  )
}

// ── ENTRY-TIME FORMATTING ──────────────────────────────────
// Compact right-column text. Group headers already provide the
// day-of-month/year context, so the cell only shows the day
// number — plus an HH:mm time if the task has a real time
// component (non-midnight). Case deadlines never show a time
// even if the timestamp has one — backend treats them as
// whole-day deadlines so the time would be meaningless.
const formatEntryTime = (entry: CalendarEntry): string => {
  const d = new Date(entry.date)
  // Timed items (appointments / timed tasks / timed Outlook) show their LOCAL
  // day — the same rule the grids use (entryDayKey); whole-day items keep UTC.
  const dayPart = entryDayKey(entry).slice(8, 10).replace(/^0/, '')
  // Check UTC time, not local. The backend stores date-only
  // deadlines at midnight UTC; in any non-UTC timezone (e.g. BST)
  // those would otherwise misread as "01:00 local" and wrongly
  // display a time on what is semantically a date-only entry.
  if (entry.type === 'task' && (d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0)) {
    const time = d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    return `${dayPart} · ${time}`
  }
  return dayPart
}

// ── GROUPING ───────────────────────────────────────────────
// Buckets entries into ordered groups based on the diff in DAYS
// (not milliseconds — that loses to DST and timezone bumps)
// between the entry's date and TODAY-AT-LOCAL-MIDNIGHT.
//
// Buckets, in display order:
//   diffDays  <   0  → "Overdue"
//   diffDays === 0  → "Today"
//   diffDays === 1  → "Tomorrow"
//   diffDays 2..7   → "Weekday DD Month"   (e.g. "Friday 5 June")
//   diffDays >= 8   → "Month YYYY"         (e.g. "August 2026")
//
// Entries arrive pre-sorted ascending from the backend, so a
// single forward pass preserves within-group order. We use a
// Map<string, CalendarEntry[]> to preserve insertion order (Map
// iteration is spec-guaranteed insertion order, unlike Object).
const groupCalendarEntries = (
  entries: CalendarEntry[],
): Array<{ label: string; entries: CalendarEntry[] }> => {
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)
  const groups = new Map<string, CalendarEntry[]>()
  for (const entry of entries) {
    const entryDay = new Date(entry.date)
    entryDay.setHours(0, 0, 0, 0)
    const diffDays = Math.round(
      (entryDay.getTime() - todayStart.getTime()) / 86400000,
    )
    let label: string
    if (diffDays < 0) {
      label = 'Overdue'
    } else if (diffDays === 0) {
      label = 'Today'
    } else if (diffDays === 1) {
      label = 'Tomorrow'
    } else if (diffDays <= 7) {
      label = entryDay.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    } else {
      label = entryDay.toLocaleDateString('en-GB', {
        month: 'long',
        year: 'numeric',
      })
    }
    const bucket = groups.get(label)
    if (bucket) {
      bucket.push(entry)
    } else {
      groups.set(label, [entry])
    }
  }
  return Array.from(groups, ([label, bucketEntries]) => ({
    label,
    entries: bucketEntries,
  }))
}

// ── ENTRY ROW ──────────────────────────────────────────────
// Conditional button-vs-div based on click eligibility. Both
// variants share the same inner layout — the wrapping element
// is the only thing that swaps. cursor-default is added when
// non-clickable so users don't see a pointer mid-row even if
// the hover border didn't already telegraph clickability.
interface CalendarRowProps {
  entry: CalendarEntry
  onOpenCase: (caseId: string) => void
  onOpenTask: (taskId: string) => void
  onOpenClient: (clientId: string) => void
}
const CalendarRow: React.FC<CalendarRowProps> = ({ entry, onOpenCase, onOpenTask, onOpenClient }) => {
  // ★ Locked-link UX: can the viewer open this entry's case? (batched + cached)
  const caseLocked = useCaseAccess([entry.caseId])(entry.caseId) === 'locked'
  const baseClass =
    'bg-casevo-surface border border-casevo-border rounded-lg transition-colors'
  const interactiveClass = 'hover:border-casevo-mid focus:outline-none focus-visible:border-casevo-mid'
  const inner = (
    <>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <TypePill type={entry.type} />
          <span className="text-sm text-casevo-forest font-medium truncate">
            {entry.title}
          </span>
        </div>
        {entry.caseLabel && (
          <p className={`text-xs truncate flex items-center gap-1 ${caseLocked ? 'text-casevo-text-soft' : 'text-casevo-mid'}`}
            title={caseLocked ? LOCKED_CASE_TIP : undefined}>
            {caseLocked && <LockIcon className="w-3 h-3" />}{entry.caseLabel}
          </p>
        )}
      </div>
      <span className="text-xs text-casevo-mid shrink-0 mt-0.5">
        {formatEntryTime(entry)}
      </span>
    </>
  )
  // Case deadline — whole row opens the case.
  // Locked case → the row is informational only (no dead-end click).
  if (entry.type === 'case_deadline' && entry.caseId && caseLocked) {
    return (
      <div className={`${baseClass} px-4 py-3 flex justify-between items-start gap-3 cursor-not-allowed opacity-80`}
        title={LOCKED_CASE_TIP} aria-disabled="true">
        {inner}
      </div>
    )
  }
  if (entry.type === 'case_deadline' && entry.caseId) {
    const caseId = entry.caseId
    return (
      <button
        type="button"
        onClick={() => onOpenCase(caseId)}
        className={`w-full text-left ${baseClass} ${interactiveClass} px-4 py-3 flex justify-between items-start gap-3`}
      >
        {inner}
      </button>
    )
  }
  // Task — text-area opens the task, secondary button (if any)
  // opens the linked case or client. Sibling buttons inside a
  // wrapping div so the secondary click doesn't bubble to the
  // primary (mirrors DocumentSearchView's download-button layout).
  //
  // Deep-link contract: primary click routes based on entry.caseId.
  //   - task WITH caseId → onOpenCase(entry.caseId) → routes to
  //     case-at-deadlines via handleOpenCase's caseInitialSection
  //     intent. The task lives against a case; the case-detail
  //     is a more useful landing than the task form.
  //   - task WITHOUT caseId → onOpenTask(entry.taskId) → routes
  //     to Tasks view with taskIntentId (highlight + scroll).
  // The secondary "Open case →" for tasks-with-cases now leads to
  // the same destination as primary (both open at deadlines) —
  // redundant but harmless; removing it is a UX polish for a
  // follow-up, not this stage.
  if (entry.type === 'task' && entry.taskId) {
    const taskId = entry.taskId
    const hasSecondary = entry.caseId !== null || entry.clientId !== null
    // A task on a locked case opens the TASK (which the viewer can see), not
    // the case they can't.
    const primaryOnClick = entry.caseId && !caseLocked
      ? () => onOpenCase(entry.caseId!)
      : () => onOpenTask(taskId)
    return (
      <div className={`${baseClass} ${interactiveClass} flex items-stretch`}>
        <button
          type="button"
          onClick={primaryOnClick}
          className="flex-1 text-left px-4 py-3 flex justify-between items-start gap-3 focus:outline-none casevo-focus min-w-0 hover:bg-casevo-mint/30 transition-colors"
        >
          {inner}
        </button>
        {hasSecondary && entry.caseId && caseLocked && (
          <span
            title={LOCKED_CASE_TIP}
            aria-disabled="true"
            className="px-3 flex items-center justify-center gap-1 text-xs text-casevo-text-soft opacity-70 border-l border-casevo-border cursor-not-allowed whitespace-nowrap"
          >
            <LockIcon className="w-3 h-3" />Case locked
          </span>
        )}
        {hasSecondary && entry.caseId && !caseLocked && (
          <button
            type="button"
            onClick={() => onOpenCase(entry.caseId!)}
            className="px-3 flex items-center justify-center text-xs text-casevo-mid hover:text-casevo-forest hover:bg-casevo-mint/30 border-l border-casevo-border focus:outline-none focus-visible:text-casevo-forest transition-colors whitespace-nowrap"
          >
            Open case →
          </button>
        )}
        {hasSecondary && !entry.caseId && entry.clientId && (
          <button
            type="button"
            onClick={() => onOpenClient(entry.clientId!)}
            className="px-3 flex items-center justify-center text-xs text-casevo-mid hover:text-casevo-forest hover:bg-casevo-mint/30 border-l border-casevo-border focus:outline-none focus-visible:text-casevo-forest transition-colors whitespace-nowrap"
          >
            Open client →
          </button>
        )}
      </div>
    )
  }
  // Fallback — shouldn't reach here in practice but safe display.
  return (
    <div className={`${baseClass} px-4 py-3 flex justify-between items-start gap-3 cursor-default`}>
      {inner}
    </div>
  )
}

// ── MAIN COMPONENT ─────────────────────────────────────────
const CalendarView: React.FC = () => {
  const calendarEntries = useCasevoStore((s) => s.calendarEntries)
  const calendarError = useCasevoStore((s) => s.calendarError)
  const loadCalendarEntries = useCasevoStore((s) => s.loadCalendarEntries)
  // Filter out done tasks — Calendar is forward-looking; once a task
  // is marked done, it lives on the Tasks list (where the user can
  // see completed work and dismiss with the × button). Case deadlines
  // and non-done tasks both stay.
  const visibleEntries = useMemo(() => {
    if (!calendarEntries) return null
    return calendarEntries.filter((entry) => entry.type !== 'task' || entry.status !== 'done')
  }, [calendarEntries])

  // ── GRID MONTH STATE (Stage 2) ───────────────────────────
  // The grid drives its own month-scoped fetch (calendar entries +
  // day-notes for the visible window), independent of the store's
  // whole-calendar load that feeds the preserved list. monthDate is
  // any date within the displayed month; nav shifts it by one month.
  // Seed from the persisted anchor/month (not always today) so returning to
  // the calendar after a create round-trip that remounted this view (task →
  // TasksView, "Open case →") restores the day/month the user was on.
  const [monthDate, setMonthDate] = useState<Date>(() => readMonthDate())
  // ── VIEW MODE: Day / Week / Month (Diary batch) ──────────
  // Persisted (localStorage). Month keeps the grid; Day/Week render the
  // time-grid. anchorDate (local-midnight) is the focus day for Day/Week
  // (the week is the Mon-first week containing it); Month uses monthDate.
  const calendarViewMode = useCalendarViewPref((s) => s.view)
  const setCalendarViewMode = useCalendarViewPref((s) => s.setView)
  const [anchorDate, setAnchorDate] = useState<Date>(() => readAnchorDate())
  // Persist the anchor day + month whenever they change, so a create that
  // remounts CalendarView (task → TasksView, "Open case →") restores the
  // SAME day/month on return instead of snapping to today. (The view mode
  // is already persisted by calendarViewPref, so view + anchor are kept
  // together.) Applies to EVERY create path — task, deadline, appointment,
  // note, reminder — since they all come back to this remounted view.
  useEffect(() => { writeAnchorDate(anchorDate) }, [anchorDate])
  useEffect(() => { writeMonthDate(monthDate) }, [monthDate])
  // The mode + focus day are part of the calendar's place (?view=calendar&
  // cal=week&date=YYYY-MM-DD): a refresh or Back/Forward to this calendar
  // lands on the same view and day. Kept in step by replacing the entry —
  // paging through days/weeks is browsing, not a history step.
  const placeDayKey = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const applyPlaceDate = (v: string | null): void => {
    const m = v ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(v) : null
    if (!m) return
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    if (Number.isNaN(d.getTime())) return
    setAnchorDate(d)
    setMonthDate(new Date(d.getFullYear(), d.getMonth(), 1))
  }
  const applyPlaceMode = (v: string | null): void => {
    if (v === 'day' || v === 'week' || v === 'month') setCalendarViewMode(v)
  }
  useEffect(() => {
    applyPlaceMode(initialPlaceParam('cal'))
    applyPlaceDate(initialPlaceParam('date'))
    // Mount only: the place this calendar was opened / restored on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  usePlaceParam('cal', calendarViewMode, applyPlaceMode)
  usePlaceParam('date', placeDayKey(calendarViewMode === 'month' ? monthDate : anchorDate), applyPlaceDate)
  // Jump-to-date popover (Stage 2). Picking a date is VIEW-AWARE: Month
  // jumps to that month; Day/Week anchor that day (Week shows its Mon-first
  // week). The mini-calendar seeds from the current focus (monthDate for
  // Month, anchorDate for Day/Week).
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const handleJumpToDate = (date: Date): void => {
    if (calendarViewMode === 'month') {
      setMonthDate(new Date(date.getFullYear(), date.getMonth(), 1))
    } else {
      setAnchorDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()))
    }
    setDatePickerOpen(false)
  }
  // Stage 3: jump into DAY view anchored on a date — from a month day
  // NUMBER click or a week day-HEADER click. Closes any open day panel so
  // the switch is clean.
  const handleViewDay = (date: Date): void => {
    setCalendarViewMode('day')
    setAnchorDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()))
    setSelectedDate(null)
  }
  // Right-click add-menu (Stage 4). Captures the cursor + the target
  // date/hour; the menu items (built in render, where the create/view
  // handlers are in scope) reuse the existing flows. hour = clicked hour
  // (timed task) or null (all-day / month cell / day header).
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; date: Date; hour: number | null } | null>(null)
  const handleCalendarContextMenu = (e: React.MouseEvent, date: Date, hour: number | null): void => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, date, hour })
  }
  // ── APPOINTMENTS ─────────────────────────────────────────
  // The create/edit modal. 'create' seeds date + start time from the click
  // (hour → HH:00, else 09:00); 'edit' loads by id. onSaved → silentReload
  // (+ the entity-changed broadcast makes other users' calendars live).
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  const [apptForm, setApptForm] = useState<
    { mode: 'create'; seedDate: string; seedStartTime: string } | { mode: 'edit'; id: string } | null
  >(null)
  const handleNewAppointmentAt = (date: Date, hour: number | null): void => {
    setApptForm({
      mode: 'create',
      seedDate: localDayKey(date),
      seedStartTime: hour !== null ? `${String(hour).padStart(2, '0')}:00` : '09:00',
    })
  }
  const handleOpenAppointment = (appointmentId: string): void => {
    setApptForm({ mode: 'edit', id: appointmentId })
  }

  // Transient toast shown when an optimistic move is reverted after a failed
  // save (auto-dismissed).
  const [moveToast, setMoveToast] = useState<string | null>(null)
  // ★ Recurring drag → "This event / This and following / All events" prompt.
  // The dragged occurrence is already shown at its new slot (optimistic);
  // Cancel / Escape / clicking the backdrop reverts it to prevItem.
  const [recurringMove, setRecurringMove] = useState<{
    entry: CalendarEntry
    newStartISO: string
    newEndISO: string | null
    prevItem: CalendarEntry | null
  } | null>(null)
  const [recurringMoveBusy, setRecurringMoveBusy] = useState(false)

  // Drag-to-move persistence. Appointment → shift startAt (+ endAt by the same
  // delta, keeping duration); a recurring OCCURRENCE moves THAT occurrence
  // only (Stage-2 override — fetch its full fields first so the override
  // doesn't drop location/notes/assignee). Reminder → new remindAt (backend
  // re-arms the notification + syncs the day). Then silentReload re-renders
  // at the new slot (+ broadcasts for appointments, so others move it live).
  // Plain arrow (not useCallback): it closes over silentReload, which is
  // declared later in this component — a useCallback dep array would read it
  // in the TDZ. The closure body only runs on drop, by when it's defined.
  const handleMoveEntry = (entry: CalendarEntry, newStartISO: string): void => {
    const startD = new Date(newStartISO)
    // Snapshots for revert-on-failure.
    const prevEntries = monthEntries
    const prevReminders = monthReminders

    // ── OPTIMISTIC: patch local state so the item JUMPS to the new slot now
    // (no waiting on the server). Appointment/task/deadline live in
    // monthEntries; a reminder chip is mapped from monthReminders.
    if (entry.type === 'reminder' && entry.reminderId) {
      // The reminder's day is its LONDON day (as the server keys it), not the UTC one.
      const dayUtcMidnight = `${londonDayOf(startD)}T00:00:00.000Z`
      setMonthReminders((rs) => rs.map((r) => (r.id === entry.reminderId ? { ...r, remindAt: newStartISO, date: dayUtcMidnight, notified: false } : r)))
    } else if (entry.type === 'appointment' && entry.appointmentId) {
      const durMs = entry.endDate ? new Date(entry.endDate).getTime() - new Date(entry.date).getTime() : null
      const newEndISO = durMs != null ? new Date(startD.getTime() + durMs).toISOString() : null
      setMonthEntries((es) => (es ? es.map((e) => (e.type === 'appointment' && e.appointmentId === entry.appointmentId ? { ...e, date: newStartISO, endDate: newEndISO } : e)) : es))
    } else if (entry.type === 'task' && entry.taskId) {
      setMonthEntries((es) => (es ? es.map((e) => (e.type === 'task' && e.taskId === entry.taskId ? { ...e, date: newStartISO } : e)) : es))
    } else if (entry.type === 'case_deadline' && entry.deadlineId) {
      // All-day → keyed on the day (UTC-midnight of the dropped civil day).
      const dayISO = new Date(Date.UTC(startD.getFullYear(), startD.getMonth(), startD.getDate())).toISOString()
      setMonthEntries((es) => (es ? es.map((e) => (e.type === 'case_deadline' && e.deadlineId === entry.deadlineId ? { ...e, date: dayISO } : e)) : es))
    } else {
      return
    }

    // ★ RECURRING appointment → ask which occurrences to move (the item is
    // already shown at the new slot). Nothing is saved until an option is
    // chosen; Cancel reverts. A one-off appointment falls through and saves
    // immediately (no prompt).
    if (entry.type === 'appointment' && entry.appointmentId && entry.recurring) {
      const durMs = entry.endDate ? new Date(entry.endDate).getTime() - new Date(entry.date).getTime() : null
      setRecurringMove({
        entry,
        newStartISO,
        newEndISO: durMs != null ? new Date(startD.getTime() + durMs).toISOString() : null,
        prevItem: prevEntries?.find((e) => e.type === 'appointment' && e.appointmentId === entry.appointmentId) ?? null,
      })
      return
    }

    // ── PERSIST in the background; on success silentReload reconciles server
    // truth (a no-op visually — already moved); on FAILURE revert + toast.
    void (async () => {
      try {
        if (entry.type === 'reminder' && entry.reminderId) {
          await updateReminderApi(entry.reminderId, { remindAt: newStartISO })
        } else if (entry.type === 'appointment' && entry.appointmentId) {
          const durMs = entry.endDate ? new Date(entry.endDate).getTime() - new Date(entry.date).getTime() : null
          const newEndISO = durMs != null ? new Date(startD.getTime() + durMs).toISOString() : null
          if (entry.recurring) {
            // Occurrence override REPLACES the row from the body, so carry the
            // full current fields (getAppointment resolves the composite id to
            // the occurrence's current state) + the new times.
            const full = await getAppointmentApi(entry.appointmentId)
            await updateAppointmentApi(
              entry.appointmentId,
              {
                title: full.title,
                type: full.type,
                location: full.location,
                notes: full.notes,
                caseId: full.caseId,
                clientId: full.clientId,
                assignedToUserId: full.assignedToUserId,
                startAt: newStartISO,
                endAt: newEndISO,
              },
              'occurrence',
            )
          } else {
            await updateAppointmentApi(entry.appointmentId, { startAt: newStartISO, endAt: newEndISO })
          }
        } else if (entry.type === 'task' && entry.taskId) {
          await updateTaskApi(entry.taskId, { dueDate: newStartISO })
        } else if (entry.type === 'case_deadline' && entry.deadlineId) {
          const ymd = `${startD.getFullYear()}-${String(startD.getMonth() + 1).padStart(2, '0')}-${String(startD.getDate()).padStart(2, '0')}`
          await updateCaseDeadlineApi(entry.deadlineId, { dueDate: ymd })
        }
        silentReload()
      } catch (err) {
        // ★ (Batch C) Revert ONLY this item (not the whole snapshot — that
        // wiped any other move / live patch made since), then re-sync from
        // the server so the grid shows server truth.
        const sameItem = (e: CalendarEntry): boolean =>
          e.type === entry.type && (
            (entry.appointmentId != null && e.appointmentId === entry.appointmentId)
            || (entry.taskId != null && e.taskId === entry.taskId)
            || (entry.deadlineId != null && e.deadlineId === entry.deadlineId))
        const prevItem = prevEntries?.find(sameItem)
        if (prevItem) setMonthEntries((es) => (es ? es.map((e) => (sameItem(e) ? prevItem : e)) : es))
        if (entry.type === 'reminder' && entry.reminderId) {
          const prevR = prevReminders.find((r) => r.id === entry.reminderId)
          if (prevR) setMonthReminders((rs) => rs.map((r) => (r.id === entry.reminderId ? prevR : r)))
        }
        silentReload()
        // The server's own reason when it refused the date (e.g. a limitation
        // deadline moved before the accident), else the generic note.
        const why = serverErrorMessage(err, [400, 422])
        setMoveToast(why ? `${why} Change reverted.` : 'Couldn’t move that — change reverted.')
        window.setTimeout(() => setMoveToast(null), why ? 6000 : 3000)
        // eslint-disable-next-line no-console
        console.error('[CalendarView] Failed to move entry:', err)
      }
    })()
  }

  // Put the dragged recurring occurrence back where it was (Cancel / failure).
  const revertRecurringMove = (m: NonNullable<typeof recurringMove>): void => {
    if (m.prevItem) {
      const prev = m.prevItem
      setMonthEntries((es) => (es ? es.map((e) => (e.type === 'appointment' && e.appointmentId === m.entry.appointmentId ? prev : e)) : es))
    }
  }
  const cancelRecurringMove = (): void => {
    if (!recurringMove || recurringMoveBusy) return
    revertRecurringMove(recurringMove)
    setRecurringMove(null)
  }
  const applyRecurringMove = async (scope: RecurringMoveScope): Promise<void> => {
    const m = recurringMove
    if (!m || !m.entry.appointmentId || recurringMoveBusy) return
    setRecurringMoveBusy(true)
    try {
      await moveAppointmentApi(m.entry.appointmentId, scope, m.newStartISO, m.newEndISO)
      setRecurringMove(null)
      // Server truth: "all" / "following" move many occurrences at once.
      silentReload()
    } catch (err) {
      revertRecurringMove(m)
      setRecurringMove(null)
      const serverMsg = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error
      setMoveToast(typeof serverMsg === 'string' && serverMsg ? `${serverMsg} — change reverted.` : 'Couldn’t move that — change reverted.')
      window.setTimeout(() => setMoveToast(null), 3500)
      // eslint-disable-next-line no-console
      console.error('[CalendarView] Failed to move recurring appointment:', err)
    } finally {
      setRecurringMoveBusy(false)
    }
  }
  // Escape = Cancel.
  useEffect(() => {
    if (!recurringMove) return
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') cancelRecurringMove() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // Pending delete descriptor — drives the Casevo ConfirmDialogue. null =
  // no confirmation open. applyTo only meaningful for appointments (the menu
  // sets 'occurrence' vs 'series').
  const [pendingDelete, setPendingDelete] = useState<
    { kind: 'task' | 'reminder' | 'appointment' | 'deadline'; id: string; applyTo?: 'series' | 'occurrence' } | null
  >(null)

  // Right-click item-menu actions. Deletes confirm first. All refetch on
  // success (+ appointment/task mutations broadcast entity-changed, so other
  // firm members' calendars update live).
  const handleCompleteTask = (taskId: string): void => {
    void updateTaskApi(taskId, { status: 'done' }).then(silentReload).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[CalendarView] complete task failed:', err)
      setMoveToast('Couldn’t complete that task — please try again.') // ★ (audit) was silent
    })
  }
  // Delete confirmations go through the Casevo ConfirmDialogue (not the native
  // browser confirm). A single pendingDelete descriptor drives one dialogue;
  // the this-occurrence-vs-whole-series choice for recurring appointments is
  // already made in the item menu (CalendarTimeGrid splits it into two menu
  // items), so each path here is a simple yes/no carrying its applyTo.
  const handleDeleteTask = (taskId: string): void => {
    setPendingDelete({ kind: 'task', id: taskId })
  }
  const handleDeleteReminder = (reminderId: string): void => {
    setPendingDelete({ kind: 'reminder', id: reminderId })
  }
  const handleDeleteAppointment = (appointmentId: string, applyTo: 'series' | 'occurrence'): void => {
    setPendingDelete({ kind: 'appointment', id: appointmentId, applyTo })
  }
  const handleCompleteDeadline = (deadlineId: string): void => {
    void updateCaseDeadlineApi(deadlineId, { completed: true }).then(silentReload).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[CalendarView] complete deadline failed:', err)
      setMoveToast('Couldn’t complete that deadline — please try again.') // ★ (audit) was silent
    })
  }
  const handleDeleteDeadline = (deadlineId: string): void => {
    setPendingDelete({ kind: 'deadline', id: deadlineId })
  }
  // Runs the actual delete for whatever is pending, then closes the dialogue.
  const confirmPendingDelete = (): void => {
    const pd = pendingDelete
    setPendingDelete(null)
    if (!pd) return
    const done = (label: string) => (err: unknown) => {
      // eslint-disable-next-line no-console
      console.error(`[CalendarView] delete ${label} failed:`, err)
      setMoveToast(`Couldn’t delete that ${label} — please try again.`) // ★ (audit) was silent
    }
    if (pd.kind === 'task') {
      void deleteTaskApi(pd.id).then(silentReload).catch(done('task'))
    } else if (pd.kind === 'reminder') {
      void deleteReminderApi(pd.id).then(silentReload).catch(done('reminder'))
    } else if (pd.kind === 'appointment') {
      void deleteAppointmentApi(pd.id, pd.applyTo ?? 'series').then(silentReload).catch(done('appointment'))
    } else {
      void deleteCaseDeadlineApi(pd.id).then(silentReload).catch(done('deadline'))
    }
  }
  // Right-click "Add reminder" from an HOUR slot pre-fills the day-panel
  // reminder composer's time. null = no pre-fill (whole-day). Cleared on
  // every OTHER panel-open path so a stale time never leaks in.
  const [reminderPrefillTime, setReminderPrefillTime] = useState<string | null>(null)
  // Open the day panel WITHOUT a reminder-time pre-fill (left-click a day,
  // "Add note", "Open day panel").
  const openDayPanel = (date: Date): void => {
    setReminderPrefillTime(null)
    setSelectedDate(date)
  }
  // Bumped by the retry button (or a permissions change) to reload the range,
  // bypassing the cache. The range's data itself comes from
  // useCalendarRangeData below (all sources in parallel, revealed together).
  const [reloadNonce, setReloadNonce] = useState(0)

  // ── DAY PANEL STATE (Stage 3) ────────────────────────────
  // selectedDate (local-midnight of the clicked civil day) drives the
  // slide-in day panel. deadlinePickerDate holds the day awaiting a
  // case choice for "New deadline" (deadlines are case-scoped).
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [deadlinePickerDate, setDeadlinePickerDate] = useState<Date | null>(null)
  const setPendingNewTaskDueDate = useCasevoStore((s) => s.setPendingNewTaskDueDate)
  const setPendingNewTaskDueTime = useCasevoStore((s) => s.setPendingNewTaskDueTime)
  const setPendingNewDeadlineDueDate = useCasevoStore((s) => s.setPendingNewDeadlineDueDate)

  // ── DARK FOCUS MODE (Stage 4) ────────────────────────────
  // Calendar-scoped dark theme, persisted in localStorage. Applied as a
  // `dark` class on the calendar content shell (below the app header),
  // so every casevo-* token inside re-themes via the CSS-var swap while
  // the rest of the app is untouched.
  const calendarDark = useCalendarDarkPref((s) => s.dark)
  const toggleCalendarDark = useCalendarDarkPref((s) => s.setDark)

  // ── MINE / FIRM-WIDE SCOPE ───────────────────────────────
  // Persisted (localStorage). 'mine' = my tasks + deadlines on my cases;
  // 'firm' = everything I'm permitted to see (role-scoped tasks + firm
  // deadlines). Passed to /api/calendar; changing it refetches the month.
  const calendarScope = useCalendarScopePref((s) => s.scope)
  const setCalendarScope = useCalendarScopePref((s) => s.setScope)
  // The Mine/Firm toggle is ADMIN-ONLY: a non-admin's tasks are already
  // own-scoped server-side, so the toggle isn't meaningful for them. They
  // get a fixed 'firm' scope — their own tasks + firm-wide deadlines — and
  // no toggle. effectiveScope is what actually drives the fetch.
  const isAdmin = useCasevoStore((s) => s.authUser?.role === 'admin')
  // Stage 2 (UX-only gate): the Mine/Firm toggle is meaningful for an admin OR
  // a non-admin GRANTED seeFirmWideTasks — for them 'firm' now shows firm-wide
  // tasks (the backend resolver is the real gate; this just avoids showing a
  // toggle that would do nothing). A non-admin WITHOUT it stays fixed to 'firm'
  // (= own tasks + firm deadlines, unchanged).
  const canSeeFirmWideTasks = useCasevoStore((s) => s.authUser?.effectivePermissions?.seeFirmWideTasks ?? false)
  const canToggleScope = isAdmin || canSeeFirmWideTasks
  const effectiveScope = canToggleScope ? calendarScope : 'firm'

  const monthKey = `${monthDate.getFullYear()}-${monthDate.getMonth()}`
  const anchorKey = ymdLocal(anchorDate)
  // Combined key identifying the current view + date across ALL views
  // (month key for Month; anchor day for Day/Week). Recomputes the fetch
  // window below.
  const viewKey = `${calendarViewMode}:${monthKey}:${anchorKey}`

  // ── RANGE DATA (no pop-in) ───────────────────────────────
  // Everything the grid shows for the visible window — tasks, deadlines,
  // appointments (/api/calendar), day-notes, reminders and this user's Outlook
  // events — is loaded by useCalendarRangeData: all sources fire in PARALLEL
  // the moment the view / date / scope changes, the grid shows a skeleton in
  // every day cell until the range is ready, and the items are revealed
  // TOGETHER (Outlook may trail after 1.5 s, with a "Loading Outlook…" marker).
  // Seen ranges show instantly from the cache and refresh quietly; the
  // previous + next range for the view are prefetched; a response for a range
  // the user has left is ignored. Outlook is personal (Graph /me) and the
  // cache is owner-scoped (firm + user), so it never leaks between users.
  const authUserId = useCasevoStore((s) => s.authUser?.id ?? null)
  const authFirmId = useCasevoStore((s) => s.authUser?.firmId ?? null)
  const cacheOwner = authUserId && authFirmId ? `${authFirmId}:${authUserId}` : null
  const fetchRange = useMemo(
    () => computeFetchRange(calendarViewMode, monthDate, anchorDate),
    // viewKey captures view + month + anchor day.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewKey],
  )
  const prefetchRanges = useMemo(
    () => neighbourFetchRanges(calendarViewMode, monthDate, anchorDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewKey],
  )
  // ★ Live permission change (store.permissionsVersion): cached entries were
  // filtered under the OLD permissions, so drop the cache and force a reload
  // (same path as the Retry button).
  const permissionsVersion = useCasevoStore((s) => s.permissionsVersion)
  const permVersionRef = useRef(permissionsVersion)
  useEffect(() => {
    if (permVersionRef.current === permissionsVersion) return
    permVersionRef.current = permissionsVersion
    calendarRangeCache.clear()
    setReloadNonce((n) => n + 1)
  }, [permissionsVersion])
  const rangeData = useCalendarRangeData({
    range: fetchRange,
    rangeId: `${calendarViewMode}:${fetchRange.from}|${fetchRange.to}`,
    scope: effectiveScope,
    owner: cacheOwner,
    neighbours: prefetchRanges,
    reloadNonce,
  })
  const monthEntries = rangeData.entries
  const monthNotes = rangeData.notes
  const monthReminders = rangeData.reminders
  const monthOutlook = rangeData.outlookEvents
  const monthError = rangeData.error
  const rangeLoading = rangeData.loading
  const setMonthEntries = rangeData.setEntries
  const setMonthReminders = rangeData.setReminders
  const outlookConnected = rangeData.outlookConnected === true
  const outlookNeedsReconnect = rangeData.outlookNeedsReconnect

  // Month navigation.
  const goPrevMonth = useCallback(() => {
    setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))
  }, [])
  const goNextMonth = useCallback(() => {
    setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))
  }, [])
  const goToday = useCallback(() => {
    const now = new Date()
    setMonthDate(new Date(now.getFullYear(), now.getMonth(), 1))
  }, [])

  // Day/Week navigation — shifts the anchor day by the view's unit
  // (7 days for Week, 1 for Day). "Today" re-anchors on the current day.
  const goPrevRange = useCallback(() => {
    const step = calendarViewMode === 'week' ? 7 : 1
    setAnchorDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - step))
  }, [calendarViewMode])
  const goNextRange = useCallback(() => {
    const step = calendarViewMode === 'week' ? 7 : 1
    setAnchorDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + step))
  }, [calendarViewMode])
  const goTodayRange = useCallback(() => {
    const now = new Date()
    setAnchorDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()))
  }, [])

  // The date-set + heading label the time-grid renders (Day = 1 day,
  // Week = the Mon-first week of the anchor).
  const timeGridDays = useMemo<Date[]>(
    () => (calendarViewMode === 'week' ? weekDaysOf(anchorDate) : [anchorDate]),
    [calendarViewMode, anchorDate],
  )
  const timeGridRangeLabel = useMemo<string>(() => {
    if (calendarViewMode === 'week') {
      const w = weekDaysOf(anchorDate)
      const a = w[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      const b = w[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      return `${a} – ${b}`
    }
    return anchorDate.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }, [calendarViewMode, anchorDate])

  // Silent refetch — quietly refreshes what's on screen (entries + notes +
  // reminders) WITHOUT the skeleton, and marks other cached ranges stale so
  // they re-validate when next shown. Out-of-date answers are dropped by the
  // hook. Used by live-freshness + every write (note / reminder / move / …).
  const refreshRange = rangeData.refresh
  const silentReload = useCallback(() => { refreshRange() }, [refreshRange])
  // Focus / visibility / poll also re-read Outlook (skipping the server's
  // 60 s cache) — Outlook has no push into Casevo.
  const refreshWithOutlook = useCallback(() => { refreshRange({ outlook: true }) }, [refreshRange])

  // Keep Outlook events fresh WITHOUT a manual refresh. Unlike tasks /
  // deadlines / appointments (which arrive live on the entity-changed channel),
  // Outlook has no push into Casevo — so we re-fetch on the natural "looking at
  // it again" triggers, mirroring the notification bell: the instant the tab
  // becomes visible or regains focus (you may have just added an event in
  // Outlook in another tab), plus a gentle poll while open. Only when connected
  // (no connection → nothing to pull, no Graph calls). View/date changes
  // already refetch Outlook via the main range fetch above; silentReload
  // refetches the whole window (Outlook included) without a skeleton flash.
  useEffect(() => {
    if (!outlookConnected) return
    const onVisible = (): void => { if (document.visibilityState === 'visible') refreshWithOutlook() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', refreshWithOutlook)
    // Conservative 5-minute poll — Graph is rate-limited (far slower than the
    // bell's 45s), background tabs throttle it, and the focus/visible refetch
    // already covers the return-to-tab case; the poll only catches a tab left
    // open + foregrounded for a long stretch.
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') refreshWithOutlook()
    }, 5 * 60 * 1000)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', refreshWithOutlook)
      window.clearInterval(id)
    }
  }, [outlookConnected, refreshWithOutlook])

  // ── LIVE-FRESHNESS (Stage 4) ─────────────────────────────
  // Reflect OTHER firm users' task / deadline / case changes on the grid
  // without a manual refresh. The entity-changed channel is echo-
  // suppressed upstream, so the local user's own creates/edits don't
  // arrive here. The calendar stays MOUNTED behind the task / appointment
  // modals, so this user's OWN task / appointment writes come in on the
  // same-tab channel (services/localEntityChanges, published by casevoApi)
  // and refresh straight away. A socket RE-connect refetches too (events
  // sent while it was down are lost). A light debounce coalesces bursts
  // (e.g. a bulk edit) into one fetch; own writes use a much shorter one.
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (CALENDAR_MODE !== 'grid') return
    const schedule = (delay: number): void => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null
        silentReload()
      }, delay)
    }
    const offs = [
      presence.subscribeEntityChanged((e) => {
        // Diary batch: 'dayNote' joins task/deadline/case so a firm-wide
        // note another user adds/edits/removes refreshes this calendar
        // (pin + day-panel) without a manual refresh.
        if (e.entity !== 'task' && e.entity !== 'deadline' && e.entity !== 'case' && e.entity !== 'dayNote' && e.entity !== 'appointment') return
        schedule(400)
      }),
      subscribeLocalEntityChanges(() => schedule(50)),
      presence.subscribeReconnected(() => schedule(400)),
    ]
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      offs.forEach((off) => off())
    }
  }, [silentReload])
  // (D12 live updates) Firm-wide calendar / task / deadline changes (incl. the
  // actor's other tabs, which entity-changed skips) → the same silent reload.
  useFirmChange(['calendar', 'tasks', 'cases', 'deadlineTypes'], () => {
    if (CALENDAR_MODE === 'grid') silentReload()
    else void useCasevoStore.getState().loadCalendarEntries()
  })

  // ── DAY PANEL: filtering + handlers (Stage 3) ────────────
  // Civil-day key of a clicked cell date (built local-midnight by the
  // grid → LOCAL parts) and of an entry/note (stored midnight-UTC →
  // UTC parts). The civil integers match, so they key the same day.
  const localDayKey = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const utcDayKey = (iso: string): string => {
    const d = new Date(iso)
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
  }

  // Entries + notes for the selected day (recomputed only when the day
  // or the month data changes).
  const dayNotesForDay = useMemo<DayNote[]>(() => {
    if (!selectedDate) return []
    const key = localDayKey(selectedDate)
    return monthNotes.filter((n) => utcDayKey(n.date) === key)
  }, [selectedDate, monthNotes])
  const dayRemindersForDay = useMemo<Reminder[]>(() => {
    if (!selectedDate) return []
    const key = localDayKey(selectedDate)
    return monthReminders.filter((r) => reminderDayKey(r) === key)
  }, [selectedDate, monthReminders])

  // Reminders as VISIBLE calendar items. Map each personal reminder into a
  // CalendarEntry (type:'reminder') so the month + time grids render it as a
  // chip/block alongside tasks/deadlines/appointments — a timed reminder
  // (remindAt set) lands on its hour, a whole-day one in the all-day strip.
  // `date` = remindAt when timed, else the whole-day date, so the grids'
  // existing timed/all-day split + hour placement work unchanged. Reminders
  // are self-scoped (fetched with `where userId`), so only the author ever
  // sees these — they never leak onto another user's calendar.
  const reminderEntries = useMemo<CalendarEntry[]>(
    () =>
      monthReminders.map((r) => ({
        type: 'reminder' as const,
        date: r.remindAt ?? r.date,
        title: r.text,
        caseId: null,
        clientId: null,
        caseLabel: null,
        taskId: null,
        status: null,
        assigneeName: null,
        reminderId: r.id,
      })),
    [monthReminders],
  )
  // Outlook events as READ-ONLY calendar items. Map each normalized Outlook
  // event into a CalendarEntry (type:'outlook') so the grids render it exactly
  // where it belongs: `date` = start (a timed event lands on its hour), an
  // all-day event carries isAllDay so it goes to the all-day strip; `endDate`
  // sizes the timed block; `webLink` is the only action (open in Outlook).
  // These are always THIS user's own mailbox (personal, like reminders) — so,
  // like reminders, they're merged regardless of the mine/firm scope and never
  // appear on another user's calendar.
  const outlookEntries = useMemo<CalendarEntry[]>(
    () =>
      monthOutlook.map((ev) => ({
        type: 'outlook' as const,
        date: ev.start,
        endDate: ev.end,
        title: ev.title,
        caseId: null,
        clientId: null,
        caseLabel: null,
        taskId: null,
        status: null,
        assigneeName: null,
        location: ev.location,
        outlookId: ev.id,
        webLink: ev.webLink,
        isAllDay: ev.isAllDay,
      })),
    [monthOutlook],
  )
  // Grid feed = the fetched entries PLUS the reminder items PLUS the Outlook
  // events. The day panel keeps its own "Remind me" section
  // (dayRemindersForDay), so reminders are deliberately NOT added to
  // dayEntries (that would duplicate every reminder inside the panel); Outlook
  // events ARE added to dayEntries (below) so the panel matches the grid.
  const gridEntries = useMemo<CalendarEntry[]>(
    () => (monthEntries ? [...monthEntries, ...reminderEntries, ...outlookEntries] : []),
    [monthEntries, reminderEntries, outlookEntries],
  )
  // The day panel's items: the fetched Casevo entries PLUS this user's
  // Outlook events for the day — bucketed with the SAME entryDayKey the
  // grids use (timed → local day of the start; all-day → its stored day), so
  // the panel always lists exactly what the grid shows on that cell. (Outlook
  // used to be grid-only, so a meeting visible on the grid was missing here.)
  // Reminders stay out: the panel has its own "Remind me" section.
  const dayEntries = useMemo<CalendarEntry[]>(() => {
    if (!selectedDate || !monthEntries) return []
    const key = localDayKey(selectedDate)
    return [...monthEntries, ...outlookEntries].filter((e) => entryDayKey(e) === key)
  }, [selectedDate, monthEntries, outlookEntries])

  const handleDayClick = useCallback((date: Date) => {
    setReminderPrefillTime(null) // left-click day → panel, no time pre-fill
    setSelectedDate(date)
  }, [])

  // Ensure firm users are loaded so the day-panel can name the authors
  // of firm-wide day-notes (load-once; no-op if already loaded).
  useEffect(() => {
    void useCasevoStore.getState().loadFirmUsers()
  }, [])

  // Diary batch (?day cold-open): if App.tsx stashed a target day
  // (pendingCalendarDate, from a ?day= deep-link such as the reminder
  // email CTA), open Day view anchored on it + open its day panel, then
  // clear the slot. Runs once on mount.
  useEffect(() => {
    const pending = useCasevoStore.getState().pendingCalendarDate
    if (!pending) return
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(pending)
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      setCalendarViewMode('day')
      setAnchorDate(d)
      setSelectedDate(d)
    }
    useCasevoStore.getState().setPendingCalendarDate(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Appointment-invite deep-link: open the appointment's VIEW when the store
  // slot is set (from a bell notification). Reactive selector so it fires even
  // when CalendarView is already mounted; cleared after opening.
  const pendingApptId = useCasevoStore((s) => s.pendingCalendarAppointmentId)
  useEffect(() => {
    if (!pendingApptId) return
    handleOpenAppointment(pendingApptId)
    useCasevoStore.getState().setPendingCalendarAppointmentId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApptId])

  // "New task" on the selected day — seed the store slot with the civil
  // day string, then navigate to TasksView's create form (which reads
  // and clears the slot). Mirrors handleOpenTask's flush + history push.
  const handleNewTaskOnDay = async (): Promise<void> => {
    if (!selectedDate) return
    await Promise.all([flushAutoSave(), flushClientAutoSave(), flushClientNotesAutoSave(), flushWitnessAutoSave(), flushDeadlineAutoSave()])
    const store = useCasevoStore.getState()
    // The task form is a MODAL over the still-mounted calendar: no history
    // step (closing it isn't navigation; the place stays the calendar).
    setPendingNewTaskDueDate(localDayKey(selectedDate))
    setPendingNewTaskDueTime(null) // day-panel create is whole-day
    store.setTaskOpenedFrom('calendar')
    setSelectedDate(null)
    // Keep the calendar MOUNTED behind the create overlay (App renders both) —
    // same as handleOpenTask, so the create form dims the calendar behind it.
    store.setShowTasks(true)
  }

  // Click-to-add from the Day/Week time-grid. hour set → a TIMED task
  // (dueDate = that date at that hour, renders on the slot); hour null →
  // an all-day task (from the all-day strip). Mirrors handleNewTaskOnDay's
  // flush + history push, plus the time slot.
  const handleNewTaskAt = async (date: Date, hour: number | null): Promise<void> => {
    await Promise.all([flushAutoSave(), flushClientAutoSave(), flushClientNotesAutoSave(), flushWitnessAutoSave(), flushDeadlineAutoSave()])
    const store = useCasevoStore.getState()
    // Modal over the calendar — no history step (see handleNewTaskOnDay).
    setPendingNewTaskDueDate(localDayKey(date))
    setPendingNewTaskDueTime(hour !== null ? `${String(hour).padStart(2, '0')}:00` : null)
    store.setTaskOpenedFrom('calendar')
    // Keep the calendar MOUNTED behind the create overlay (App renders both).
    store.setShowTasks(true)
  }
  // Click-to-add deadline from the time-grid. Deadlines are whole-day
  // (the diary always renders them in the all-day strip), so the clicked
  // hour is ignored — reuse the case-picker → date-only create flow.
  const handleNewDeadlineAt = (date: Date): void => {
    setDeadlinePickerDate(date)
  }

  // "New deadline" — deadlines are case-scoped, so first pick a case.
  // Stash the day; the picker's onPick seeds the slot + opens that case
  // at its Deadlines section (DeadlinesSection reads + clears the slot).
  const handleNewDeadlineOnDay = (): void => {
    if (!selectedDate) return
    setDeadlinePickerDate(selectedDate)
  }
  const handlePickDeadlineCase = (caseId: string): void => {
    if (deadlinePickerDate) {
      setPendingNewDeadlineDueDate(localDayKey(deadlinePickerDate))
    }
    setDeadlinePickerDate(null)
    setSelectedDate(null)
    // handleOpenCase already sets caseInitialSection='deadlines' + flushes
    // + navigates, so the case lands on its Deadlines section.
    void handleOpenCase(caseId)
  }

  // Trigger load on mount. The sidebar's handleCalendar already kicks
  // a load when the user clicks Calendar; this useEffect is a safety
  // net for cases where the view mounts without going through the
  // sidebar handler (e.g. future deep-linking). The store's loader
  // replaces the array atomically, so double-firing is harmless.
  // Only needed for the preserved LIST mode — the grid does its own
  // month-scoped fetch above.
  useEffect(() => {
    if (CALENDAR_MODE !== 'list') return
    void loadCalendarEntries()
  }, [loadCalendarEntries])

  // ← Back closes the takeover. App.tsx's renderMain then falls
  // through to whichever other flag was true (or to ViewingPane /
  // CaseListScreen if none were).
  const handleBack = (): void => {
    void navigateTo({ view: 'caseList' })
  }

  // Open-from-row handler — flush, await the case load, then close
  // this takeover so App.tsx renders the freshly-populated case detail.
  //
  // Deep-link contract update: sets caseInitialSection='deadlines'
  // BEFORE setCurrentCaseById so ViewingPane's mount effect scrolls
  // straight to the deadlines section on land. Applies to BOTH the
  // case_deadline primary click and the task-with-caseId primary
  // click — every case-open from Calendar lands at deadlines
  // (semantically correct: the user was already on a deadline-first
  // view). Old handleOpenCase(caseId) call sites don't change.
  const handleOpenCase = async (caseId: string): Promise<void> => {
    try {
      // One history step (Back returns to this calendar, same view + day).
      // INSTANT-OPEN: the case page's loading shell shows at once rather than
      // the calendar freezing for the whole fetch.
      await navigateTo({ view: 'caseDetail', caseId }, { intents: (s) => { s.setCaseOpenedFrom('calendar'); s.setCaseInitialSection('deadlines') } })
    } catch (err) {
      // Clear the intent so a subsequent successful open doesn't
      // inherit it. Same discipline as routeByItemType.
      useCasevoStore.getState().setCaseInitialSection(null)
      // eslint-disable-next-line no-console
      console.error('[CalendarView] Failed to open case:', err)
    }
  }

  // Cross-view navigation — open the Tasks view AND open THAT task's
  // DETAIL (its edit/view form). Uses pendingTaskId (TasksView's
  // "open this task" consumer at ~L247 does setEditingTask + mode
  // 'form' once tasks have loaded), NOT taskIntentId (which only
  // highlights the row in the list). Locked decision: clicking a
  // calendar task → see that task, with its linked case shown in the
  // form. The task is a modal over the calendar, so the place (and
  // history) stays the calendar.
  const handleOpenTask = async (taskId: string): Promise<void> => {
    await Promise.all([flushAutoSave(), flushClientAutoSave(), flushClientNotesAutoSave(), flushWitnessAutoSave(), flushDeadlineAutoSave()])
    const store = useCasevoStore.getState()
    // The task opens as a MODAL over the calendar: no history step.
    store.setPendingTaskId(taskId)
    store.setTaskOpenedFrom('calendar')
    // Keep showCalendar TRUE — the calendar stays MOUNTED behind the task
    // modal (App renders both when taskOpenedFrom==='calendar'), so the task
    // overlays the dimmed calendar instead of a blank page, the calendar's
    // anchor day + focus theme are preserved, and returning needs no re-fetch.
    store.setShowTasks(true)
  }
  // Cross-view navigation — open a specific client in ClientSearchView's
  // detail mode. Pre-setting clientSearchMode='detail' covers the case
  // where ClientSearchView is already mounted in search mode.
  const handleOpenClient = async (clientId: string): Promise<void> => {
    await Promise.all([flushAutoSave(), flushClientAutoSave(), flushClientNotesAutoSave(), flushWitnessAutoSave(), flushDeadlineAutoSave()])
    void navigateTo({ view: 'clientDetail', clientId }, { skipFlush: true })
  }

  // Memoised so the per-render date-math + bucket walk only runs
  // when the underlying entries array changes (not on every state
  // tick from other store slices).
  const groups = useMemo(() => {
    if (!visibleEntries || visibleEntries.length === 0) return []
    return groupCalendarEntries(visibleEntries)
  }, [visibleEntries])

  // "Loading Outlook…" — shown in the grid header only when the rest of the
  // range was revealed while Outlook (the slow Graph read) is still on its
  // way. Small, polite, reduced-motion safe.
  const outlookMarker = rangeData.outlookLoading ? (
    <span role="status" className="inline-flex items-center gap-1.5 text-xs text-casevo-mid whitespace-nowrap">
      <span
        className="inline-block w-3 h-3 rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin motion-reduce:animate-none"
        aria-hidden="true"
      />
      <span className="hidden sm:inline">Loading Outlook…</span>
      <span className="sr-only sm:hidden">Loading Outlook…</span>
    </span>
  ) : null

  return (
    <div className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
      <AppHeader
        onWordmarkClick={handleBack}
        // Dark focus mode: drop the header's light bottom border so it
        // doesn't draw a white seam against the dark diary below (the header
        // sits outside the calendar's theme wrapper, so its border stays the
        // light app-token otherwise). No-op when the diary is light.
        hideBottomBorder={calendarDark}
        leftHamburger={<SidebarHamburger />}
        leftSlot={<BackButton />}
      />
      {/* Move-failed toast (optimistic move reverted). */}
      {moveToast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-[220] max-w-[90vw]">
          <ActionBox variant="slim" tone="danger" live className="shadow-lg" message={moveToast} />
        </div>
      )}
      {/* ★ Recurring drag scope prompt. Inside the calendar's light/dark scope
          so it follows the Diary theme. Backdrop click = Cancel (reverts). */}
      {recurringMove && (
        <div className={calendarDark ? 'dark' : 'casevo-light'}>
          <div className="fixed inset-0 z-[230] flex items-center justify-center bg-black/40 px-4" onMouseDown={cancelRecurringMove}>
            <div role="dialog" aria-modal="true" aria-labelledby="recurring-move-title"
              className="w-full max-w-xs rounded-lg border border-casevo-border bg-casevo-surface shadow-xl p-5"
              onMouseDown={(e) => e.stopPropagation()}>
              <h3 id="recurring-move-title" className="text-sm font-medium text-casevo-forest">Move recurring appointment</h3>
              <p className="mt-1 text-xs text-casevo-mid">
                Move <span className="font-medium text-casevo-forest">{recurringMove.entry.title}</span> to{' '}
                {new Date(recurringMove.newStartISO).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
              <div className="mt-4 flex flex-col gap-2">
                {([
                  ['occurrence', 'This event'],
                  ['following', 'This and following events'],
                  ['series', 'All events'],
                ] as [RecurringMoveScope, string][]).map(([scope, label], i) => (
                  <button key={scope} type="button" disabled={recurringMoveBusy} autoFocus={i === 0}
                    onClick={() => { void applyRecurringMove(scope) }}
                    className="w-full text-left px-3 py-2 text-sm rounded border border-casevo-border text-casevo-forest bg-casevo-input hover:border-casevo-mid disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid">
                    {label}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <button type="button" onClick={cancelRecurringMove} disabled={recurringMoveBusy}
                  className="px-3 py-1.5 text-xs text-casevo-mid hover:text-casevo-forest disabled:opacity-50 focus:outline-none casevo-focus">
                  {recurringMoveBusy ? 'Moving…' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── GRID MODE (Stage 2 default) ─────────────────────── */}
      {/* Dark focus mode (Stage 4): the `dark` class is scoped to this
          content shell (NOT the app header above), so every casevo-*
          token inside re-themes to the dark Casevo-green palette via the
          CSS-var swap while the rest of the app stays light. The
          full-width band carries bg-casevo-offwhite so the dark ground
          fills the content area edge-to-edge. */}
      {CALENDAR_MODE === 'grid' && (
        <div className={calendarDark ? 'dark' : 'casevo-light'}>
        <div className="bg-casevo-offwhite min-h-[calc(100vh-3.5rem)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          {/* Toolbar. At phone width (≤ 640) the controls WRAP onto their own
              line(s) under the title instead of overflowing the page; from
              sm up they sit at the right exactly as before. */}
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3" data-calendar-toolbar="">
            <div>
              <p className="text-xs uppercase tracking-widest text-casevo-brand mb-1">Calendar</p>
              <h1 className="text-xl sm:text-2xl font-medium text-casevo-forest">Diary</h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end min-w-0 max-w-full" data-calendar-controls="">
              {/* Jump-to-date (Stage 2): a calendar-icon button opens a mini
                  month-calendar popover to jump to ANY date. View-aware via
                  handleJumpToDate. The ‹ › + Today live inside the grids. */}
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setDatePickerOpen((o) => !o)}
                  aria-label="Jump to date"
                  aria-expanded={datePickerOpen}
                  className={`w-8 h-8 flex items-center justify-center rounded-md border border-casevo-border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mint ${
                    datePickerOpen
                      ? 'bg-casevo-primary text-white'
                      : 'bg-casevo-surface text-casevo-mid hover:text-casevo-forest hover:bg-casevo-mint/30'
                  }`}
                >
                  {/* Calendar glyph */}
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                  </svg>
                </button>
                {datePickerOpen && (
                  <>
                    {/* Outside-click catcher. */}
                    <div className="fixed inset-0 z-40" onClick={() => setDatePickerOpen(false)} />
                    <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 z-50 bg-casevo-surface border border-casevo-border rounded-lg shadow-lg p-3">
                      <CalendarMiniMonth
                        value={calendarViewMode === 'month' ? monthDate : anchorDate}
                        onPick={handleJumpToDate}
                      />
                    </div>
                  </>
                )}
              </div>
              {/* Day / Week / Month view toggle (persisted). Month is the
                  grid; Day/Week are the time-grid. */}
              <div
                className="inline-flex rounded-md border border-casevo-border overflow-hidden text-xs shrink-0"
                role="group"
                aria-label="Calendar view"
              >
                {(['day', 'week', 'month'] as CalendarViewMode[]).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setCalendarViewMode(v)}
                    aria-pressed={calendarViewMode === v}
                    className={`px-3 py-1.5 capitalize transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mint ${
                      calendarViewMode === v
                        ? 'bg-casevo-primary text-white'
                        : 'bg-casevo-surface text-casevo-mid hover:text-casevo-forest hover:bg-casevo-mint/30'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              {/* Mine / Firm-wide scope toggle — admins OR non-admins granted
                  seeFirmWideTasks (Stage 2). A non-admin without it has
                  own-scoped tasks server-side, so the toggle isn't meaningful
                  (fixed 'firm' scope: own tasks + firm deadlines). UX-only — the
                  backend resolver enforces regardless of what's shown here. */}
              {canToggleScope && (
              <div
                className="inline-flex rounded-md border border-casevo-border overflow-hidden text-xs shrink-0"
                role="group"
                aria-label="Calendar scope"
              >
                <button
                  type="button"
                  onClick={() => setCalendarScope('mine')}
                  aria-pressed={calendarScope === 'mine'}
                  className={`px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mint ${
                    calendarScope === 'mine'
                      ? 'bg-casevo-primary text-white'
                      : 'bg-casevo-surface text-casevo-mid hover:text-casevo-forest hover:bg-casevo-mint/30'
                  }`}
                >
                  Mine
                </button>
                <button
                  type="button"
                  onClick={() => setCalendarScope('firm')}
                  aria-pressed={calendarScope === 'firm'}
                  className={`px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mint ${
                    calendarScope === 'firm'
                      ? 'bg-casevo-primary text-white'
                      : 'bg-casevo-surface text-casevo-mid hover:text-casevo-forest hover:bg-casevo-mint/30'
                  }`}
                >
                  Firm-wide
                </button>
              </div>
              )}
              {/* Light / Dark focus toggle (persisted in localStorage). */}
              <div
                className="inline-flex rounded-md border border-casevo-border overflow-hidden text-xs shrink-0"
                role="group"
                aria-label="Calendar appearance"
              >
                <button
                  type="button"
                  onClick={() => toggleCalendarDark(false)}
                  aria-pressed={!calendarDark}
                  className={`px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mint ${
                    !calendarDark
                      ? 'bg-casevo-primary text-white'
                      : 'bg-casevo-surface text-casevo-mid hover:text-casevo-forest hover:bg-casevo-mint/30'
                  }`}
                >
                  Light
                </button>
                <button
                  type="button"
                  onClick={() => toggleCalendarDark(true)}
                  aria-pressed={calendarDark}
                  className={`px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mint ${
                    calendarDark
                      ? 'bg-casevo-primary text-white'
                      : 'bg-casevo-surface text-casevo-mid hover:text-casevo-forest hover:bg-casevo-mint/30'
                  }`}
                >
                  Dark focus
                </button>
              </div>
            </div>
          </div>
          {/* Error — a load failure shows a retry banner rather than an
              empty grid. */}
          {monthError && (
            <ActionBox alert tone="danger" className="mb-4" message={monthError}
              actions={[{ label: 'Try again', kind: 'link', onClick: () => setReloadNonce((n) => n + 1) }]} />
          )}
          {/* Outlook reconnect prompt — only when the stored Microsoft token is
              dead (revoked / password change). The "Loading Outlook…" marker
              lives in the grid's own header row (no line reserved here, so
              nothing shifts when the Microsoft status arrives). */}
          {outlookNeedsReconnect && (
            <ActionBox variant="slim" tone="warn" live className="mb-3"
              title="Your Outlook connection needs attention"
              message="— your calendar isn’t syncing."
              actions={[{ label: 'Reconnect in Settings', kind: 'link', arrow: true, onClick: () => { void navigateTo({ view: 'settings', params: { section: 'email' } }) } }]} />
          )}
          {/* The grid/time-grid — shown IMMEDIATELY for every view / date. Until
              the range's data is ready each day cell shows a skeleton; then
              all its items are revealed together (Outlook may trail by design:
              after 1.5 s the rest shows and the header says "Loading Outlook…").
              Even an empty range renders the frame. Month → the month grid;
              Day/Week → the time-grid, both fed by the same fetched window +
              the same day-panel + open handlers. */}
          <div aria-busy={rangeLoading || undefined}>
          {!monthError && calendarViewMode === 'month' && (
            <CalendarMonthGrid
              monthDate={monthDate}
              loading={rangeLoading}
              fade={rangeData.fade}
              statusSlot={outlookMarker}
              entries={gridEntries}
              dayNotes={monthNotes}
              reminders={monthReminders}
              onPrevMonth={goPrevMonth}
              onNextMonth={goNextMonth}
              onToday={goToday}
              onDayClick={handleDayClick}
              onViewDay={handleViewDay}
              onContextMenu={handleCalendarContextMenu}
            />
          )}
          {!monthError && calendarViewMode !== 'month' && (
            <CalendarTimeGrid
              days={timeGridDays}
              loading={rangeLoading}
              fade={rangeData.fade}
              outlookLate={rangeData.outlookLate}
              statusSlot={outlookMarker}
              entries={gridEntries}
              dayNotes={monthNotes}
              reminders={monthReminders}
              onPrev={goPrevRange}
              onNext={goNextRange}
              onToday={goTodayRange}
              onViewDay={handleViewDay}
              onOpenTask={(id) => { void handleOpenTask(id) }}
              onOpenCase={(id) => { void handleOpenCase(id) }}
              onOpenAppointment={handleOpenAppointment}
              onOpenReminder={handleDayClick}
              onNewTaskAt={(date, hour) => { void handleNewTaskAt(date, hour) }}
              onNewDeadlineAt={(date) => { handleNewDeadlineAt(date) }}
              onNewAppointmentAt={handleNewAppointmentAt}
              onContextMenu={handleCalendarContextMenu}
              rangeLabel={timeGridRangeLabel}
              onMoveEntry={handleMoveEntry}
              onCompleteTask={handleCompleteTask}
              onDeleteTask={handleDeleteTask}
              onDeleteReminder={handleDeleteReminder}
              onDeleteAppointment={handleDeleteAppointment}
              onCompleteDeadline={handleCompleteDeadline}
              onDeleteDeadline={handleDeleteDeadline}
            />
          )}
          </div>
          {/* Slide-in day detail panel (Stage 3). */}
          {selectedDate && (
            <CalendarDayPanel
              date={selectedDate}
              entries={dayEntries}
              notes={dayNotesForDay}
              reminders={dayRemindersForDay}
              onClose={() => setSelectedDate(null)}
              onOpenCase={(id) => { void handleOpenCase(id) }}
              onOpenTask={(id) => { void handleOpenTask(id) }}
              onOpenAppointment={handleOpenAppointment}
              onNewTask={() => { void handleNewTaskOnDay() }}
              onNewAppointment={() => {
                // Month day-panel → appointment form pre-set to this day
                // (day-level, so no hour → the form's default start time,
                // which the user adjusts). Close the panel first.
                const d = selectedDate
                if (!d) return
                setSelectedDate(null)
                handleNewAppointmentAt(d, null)
              }}
              onNewDeadline={handleNewDeadlineOnDay}
              onNotesMutated={silentReload}
              onRemindersMutated={silentReload}
              reminderInitialTime={reminderPrefillTime}
            />
          )}
          {/* Case picker for "New deadline" (deadlines are case-scoped). */}
          <LinkCasePickerModal
            isOpen={deadlinePickerDate !== null}
            onClose={() => setDeadlinePickerDate(null)}
            currentCaseId=""
            excludeCaseIds={[]}
            onPick={handlePickDeadlineCase}
          />
          {/* Casevo confirm dialogue for all calendar deletes (replaces the
              native window.confirm). The occurrence-vs-series choice for
              recurring appointments is made in the item menu, so each path is
              a simple yes/no; the copy reflects which. */}
          <ConfirmDialogue
            isOpen={pendingDelete !== null}
            title={
              pendingDelete?.kind === 'task' ? 'Delete task?'
                : pendingDelete?.kind === 'reminder' ? 'Delete reminder?'
                : pendingDelete?.kind === 'deadline' ? 'Delete deadline?'
                : pendingDelete?.applyTo === 'occurrence' ? 'Delete this occurrence?'
                : 'Delete appointment?'
            }
            message={
              pendingDelete?.kind === 'appointment' && pendingDelete.applyTo === 'series'
                ? 'This deletes the whole series. This can’t be undone.'
                : 'This can’t be undone.'
            }
            confirmLabel="Delete"
            onConfirm={confirmPendingDelete}
            onCancel={() => setPendingDelete(null)}
          />
          {/* Right-click add-menu (Stage 4). Items reuse the existing
              create/view flows, all pre-set to the clicked date (+ hour for
              a timed slot). Reminder/Note open the day panel (which hosts
              both editors). Viewport-clamped by the shared menu component. */}
          {contextMenu && (
            <CalendarContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              onClose={() => setContextMenu(null)}
              title={`${contextMenu.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}${contextMenu.hour !== null ? ` · ${String(contextMenu.hour).padStart(2, '0')}:00` : ''}`}
              items={((): CalendarMenuItem[] => {
                const { date, hour } = contextMenu
                return [
                  {
                    label: hour !== null ? `Add task at ${String(hour).padStart(2, '0')}:00` : 'Add task',
                    icon: <MenuIcon name="checkSquare" />,
                    onClick: () => { void handleNewTaskAt(date, hour) },
                  },
                  { label: 'Add deadline', icon: <MenuIcon name="flag" />, onClick: () => { handleNewDeadlineAt(date) } },
                  {
                    label: hour !== null ? `Add appointment at ${String(hour).padStart(2, '0')}:00` : 'Add appointment',
                    icon: <MenuIcon name="calendar" />,
                    onClick: () => handleNewAppointmentAt(date, hour),
                  },
                  {
                    label: 'Add reminder',
                    icon: <MenuIcon name="bell" />,
                    separated: true,
                    // From an HOUR slot → pre-fill the reminder composer's
                    // time to that hour; from a day cell / all-day → no time.
                    onClick: () => {
                      setReminderPrefillTime(hour !== null ? `${String(hour).padStart(2, '0')}:00` : null)
                      setSelectedDate(date)
                    },
                  },
                  { label: 'Add note', icon: <MenuIcon name="note" />, onClick: () => openDayPanel(date) },
                  { label: 'Open day panel', icon: <MenuIcon name="panel" />, onClick: () => openDayPanel(date) },
                  { label: 'View day', icon: <MenuIcon name="day" />, separated: true, onClick: () => handleViewDay(date) },
                  { label: 'Go to today', icon: <MenuIcon name="target" />, onClick: () => handleJumpToDate(new Date()) },
                ]
              })()}
            />
          )}
          {/* Appointment create/edit modal (Stage 5). Saving refetches the
              month window (silentReload); the backend entity-changed
              broadcast makes other firm members' calendars live-update. */}
          {apptForm && (
            <AppointmentFormModal
              mode={apptForm.mode}
              appointmentId={apptForm.mode === 'edit' ? apptForm.id : undefined}
              seedDate={apptForm.mode === 'create' ? apptForm.seedDate : undefined}
              seedStartTime={apptForm.mode === 'create' ? apptForm.seedStartTime : undefined}
              firmUsers={firmUsers ?? []}
              onClose={() => setApptForm(null)}
              onSaved={silentReload}
              onOpenCase={(id) => {
                // Close the form, then open the case via the shared calendar
                // opener — it sets caseOpenedFrom='calendar', so ViewingPane's
                // Back returns to the calendar (same routing as a deadline/
                // task opened from here). No new CaseOpenedFrom value needed.
                setApptForm(null)
                void handleOpenCase(id)
              }}
            />
          )}
        </div>
        </div>
        </div>
      )}
      {/* ── LIST MODE (preserved; behind CALENDAR_MODE flag) ──── */}
      {CALENDAR_MODE === 'list' && (
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
        {/* Page heading — always shown, above the content branches */}
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-casevo-brand mb-1">Calendar</p>
          <h1 className="text-xl sm:text-2xl font-medium text-casevo-forest">Deadlines</h1>
        </div>
        {/* Branch 0: error — a load failure shows a retry banner, NOT a
            misleading "No deadlines yet" empty state. Takes precedence
            over the loading/empty branches (on error, entries is null). */}
        {calendarError && (
          <ActionBox alert tone="danger" message={calendarError}
            actions={[{ label: 'Try again', kind: 'link', onClick: () => { void loadCalendarEntries() } }]} />
        )}
        {/* Branch 1: loading — three pulsing skeleton rows */}
        {!calendarError && visibleEntries === null && (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="bg-casevo-surface border border-casevo-border rounded-lg px-4 py-3 animate-pulse"
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="w-40 h-4 bg-casevo-mint/40 rounded mb-2" />
                    <div className="w-28 h-3 bg-casevo-mint/30 rounded" />
                  </div>
                  <div className="w-12 h-3 bg-casevo-mint/30 rounded shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Branch 2: empty — slimmer ClientSearchView-style state.
            No CTA button (Calendar has nothing for the user to create
            from this view). */}
        {visibleEntries !== null && visibleEntries.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center px-4 py-16 sm:py-20 min-h-[40vh]">
            <CalendarEmptyIcon />
            <h2 className="text-casevo-forest font-medium text-xl sm:text-2xl mb-3">
              No deadlines yet
            </h2>
            <p className="text-casevo-mid text-sm sm:text-base max-w-md leading-relaxed">
              Deadlines from cases and tasks appear here, sorted by date.
            </p>
          </div>
        )}
        {/* Branch 3: grouped list — Overdue → Today → Tomorrow →
            named days → months. Overdue header is recoloured red so
            the user clocks urgency at a glance. */}
        {visibleEntries !== null && visibleEntries.length > 0 && (
          <>
            {groups.map((group) => {
              const headerColour =
                group.label === 'Overdue' ? 'text-casevo-danger' : 'text-casevo-mid'
              return (
                <div key={group.label} className="mb-6 last:mb-0">
                  <h2 className={`text-xs uppercase tracking-widest ${headerColour} mb-2`}>
                    {group.label}
                  </h2>
                  <div className="flex flex-col gap-2">
                    {group.entries.map((entry, i) => (
                      // entry keys: prefer the entry's own taskId or
                      // caseId, but fall back to index because two
                      // case_deadlines could share a caseId (multiple
                      // dates on one case) and tasks may share neither.
                      <CalendarRow
                        key={`${entry.type}-${entry.taskId ?? entry.caseId ?? ''}-${i}`}
                        entry={entry}
                        onOpenCase={(id) => { void handleOpenCase(id) }}
                        onOpenTask={(id) => { void handleOpenTask(id) }}
                        onOpenClient={(id) => { void handleOpenClient(id) }}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </>
        )}
      </div>
      )}
    </div>
  )
}
export default CalendarView
